import { useEffect, useRef, useState } from "react";
import useDelete from "../hook/useDelete";
import { delete_Lesson } from "../services/Lesson";
import useAuth from "../hook/useAuth";
import useConfirm from "../hook/useConfirm";

type LessonSideBarProps = {
    lessonList: any[];
    onSelectLesson: (lessonId: number) => void;
    selectedLessonId?: number | null;
    setShowAddLessonPopup: (value: boolean) => void;
    setShowEditLessonPopup: (value: boolean) => void;
    setEditLesson: (value: any) => void;
    setRefreshLessonList: (value: boolean) => void;
};


export const EditLessonSideBar = ({ lessonList, onSelectLesson, selectedLessonId: selectedLessonIdProp, setShowAddLessonPopup, setShowEditLessonPopup, setEditLesson, setRefreshLessonList }: LessonSideBarProps) => {
    const { auth }: any = useAuth();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
    const dropDownRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownOpen !== null &&
                dropDownRefs.current[dropdownOpen] &&
                !dropDownRefs.current[dropdownOpen]?.contains(event.target as Node)
            ) {
                setDropdownOpen(null);
            }
        }

        document.addEventListener("click", handleClickOutside); // 👈 use click, not mousedown
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [dropdownOpen]);


    useEffect(() => {
        if (isSidebarOpen) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => document.body.classList.remove("overflow-hidden");
    }, [isSidebarOpen]);

    useEffect(() => {
        if (selectedLessonIdProp) {
            setSelectedLessonId(selectedLessonIdProp);
        }
    }, [selectedLessonIdProp]);

    const handleLessonClick = (lessonId: number) => {
        setSelectedLessonId(lessonId);
        onSelectLesson(lessonId);
    };

    const handleAddLesson = () => {
        setShowAddLessonPopup(true);
    }

    const handleOnDeleteLesson = (id: number) => {
        if (id) {
            setDeletePopup(true);
            setDeleteData({
                id: id,
                tilte: `Delete`,
                summary: `Are you sure you want to delete this lesson?`,
                isDelete: (callback: any) => {
                    delete_Lesson(id, auth?.id)
                        .then(() => {
                            setDeletePopup(false);
                            setRefreshLessonList(true);
                            callback();
                        })
                        .catch(error => {
                            console.log(error);
                            callback();

                            setConfirmData({
                                title: 'Warning',
                                summary: error.response.data,
                                confirmBtn: 'Ok',
                                isConfirm: (callback: any) => {
                                    callback();
                                }
                            });

                            setConfirmPopup(true);
                        });
                }
            });
        }
    }

    return (
        <>
            <div className="md:h-[calc(100vh-200px)] thin-scrollbar overflow-y-auto overflow-x-hidden md:border-r border-[#D0D0D0] md:w-52 md:min-w-[13rem] md:max-w-[13rem] max-md:w-full flex-shrink-0 break-words">
                <div className="p-4 border-b border-[#D0D0D0] w-full max-md:hidden">
                    <button
                        className="py-2 px-4 bg-primary text-white text-sm font-medium whitespace-nowrap rounded-md outline-none hover:bg-blue-600 shadow-md"
                        onClick={() => handleAddLesson()}
                    >
                        Add Lesson
                    </button>
                </div>


                {lessonList?.map((lesson: any, index: number) => {
                    const isActive = selectedLessonId === lesson.id;
                    return (
                        <div
                            key={lesson.id}
                            className={`w-full px-6 py-2 border-b border-[#D0D0D0] cursor-pointer flex items-center space-x-3 max-md:hidden ${isActive
                                ? "text-primary font-bold bg-gray-100"
                                : "text-black hover:bg-gray-50"
                                }`}
                        >
                            <span className=" flex-1 text-sm font-semibold break-words whitespace-normal leading-snug" onClick={() => handleLessonClick(lesson.id)}>
                                {`${index + 1}: ${lesson?.title}`}
                            </span>

                            <div ref={(el) => (dropDownRefs.current[index] = el)} className="relative">

                                <button className="text-gray-600 hover:text-gray-800" onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}>
                                    <span className="material-symbols-outlined text-xl">
                                        more_horiz
                                    </span>
                                </button>

                                {dropdownOpen === index && (
                                    <div className="absolute w-40 bg-white shadow-lg border rounded-md z-[99999] left-[-154px]">
                                        <ul className="text-sm text-gray-700">

                                            <li
                                                className="px-4 py-2 hover:bg-gray-100 hover:underline cursor-pointer"
                                                onClick={() => {
                                                    setEditLesson(lesson)
                                                    setDropdownOpen(null);
                                                    setShowEditLessonPopup(true);
                                                }}
                                            >
                                                Edit
                                            </li>

                                            <li
                                                className="px-4 py-2 hover:bg-gray-100 hover:underline cursor-pointer"
                                                onClick={() => {
                                                    setDropdownOpen(null);
                                                    handleOnDeleteLesson(lesson?.id)
                                                }}
                                            >
                                                Delete
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div className="w-full p-3 border-b border-[#D0D0D0] md:hidden flex justify-end items-center">

                    {/* Mobile Menu Button */}
                    <button onClick={() => setSidebarOpen(true)}>
                        <span className="material-symbols-outlined text-3xl">menu</span>
                    </button>
                </div>
            </div>

            {isSidebarOpen && (
                <div className="fixed inset-0 z-[10000] flex">

                    {/* BACKDROP */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-40"
                        onClick={() => setSidebarOpen(false)}
                    />

                    {/* SLIDING DRAWER */}
                    <div className="relative z-[10001] w-72 bg-white h-screen shadow-xl animate-slideIn pb-12">

                        {/* Header */}
                        <div className="flex items-center justify-end p-4 border-b">

                            <button onClick={() => setSidebarOpen(false)}>
                                <span className="material-symbols-outlined text-3xl">close</span>
                            </button>
                        </div>

                        {/* Lesson Items */}
                        <div className="overflow-y-auto max-md:h-[calc(100vh-200px)]  p-2">
                            <div className="p-4 border-b border-[#D0D0D0] w-full">
                                <button
                                    className="py-2 px-4 bg-primary text-white text-sm font-medium whitespace-nowrap rounded-md outline-none hover:bg-blue-600 shadow-md"
                                    onClick={() => {
                                        setSidebarOpen(false)
                                        handleAddLesson()
                                    }}
                                >
                                    Add Lesson
                                </button>
                            </div>


                            {lessonList?.map((lesson: any, index: number) => {
                                const isActive = selectedLessonId === lesson.id;
                                return (
                                    <div
                                        key={lesson.id}
                                        className={`px-6 py-2 border-b border-[#D0D0D0] cursor-pointer flex items-center space-x-3 ${isActive
                                            ? "text-primary font-bold bg-gray-100"
                                            : "text-black hover:bg-gray-50"
                                            }`}
                                    >
                                        <span className="text-sm font-semibold" onClick={() => {
                                            setSidebarOpen(false)
                                            handleLessonClick(lesson.id)
                                        }}>
                                            {`${index + 1}: ${lesson?.title}`}
                                        </span>

                                        <div ref={(el) => (dropDownRefs.current[index] = el)} className="relative">

                                            <button className="text-gray-600 hover:text-gray-800" onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}>
                                                <span className="material-symbols-outlined text-xl">
                                                    more_horiz
                                                </span>
                                            </button>

                                            {dropdownOpen === index && (
                                                <div className="absolute w-40 bg-white shadow-lg border rounded-md top-[42px] -right-[53px] z-[1000]">
                                                    <ul className="text-sm text-gray-700">

                                                        <li
                                                            className="px-4 py-2 hover:bg-gray-100 hover:underline cursor-pointer"
                                                            onClick={() => {
                                                                setEditLesson(lesson)
                                                                setSidebarOpen(false)
                                                                setDropdownOpen(null);
                                                                setShowEditLessonPopup(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </li>

                                                        <li
                                                            className="px-4 py-2 hover:bg-gray-100 hover:underline cursor-pointer"
                                                            onClick={() => {
                                                                setDropdownOpen(null);
                                                                setSidebarOpen(false);
                                                                handleOnDeleteLesson(lesson?.id)
                                                            }}
                                                        >
                                                            Delete
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}